/**
 * One article, as the desk consults it — everything essential on one page.
 *
 * The fiche answers five questions, in this order, without a click: what it
 * is, where it is classed, its characteristics, its prices, where it is
 * available. So it is three blocks under a compact header, and tabs only for
 * what is secondary:
 *
 * - **Header** — picture, reference, short designation, publication state,
 *   and the figures a desk opens the fiche for: main price, stock, depots,
 *   companies that sell it. Each "no" is said as itself.
 * - **Identification** — references, both designations, unit, brand, ERP
 *   family, each level of the tree, and the tree path as one line, with
 *   « Modifier le classement » opening the whole tree in a side panel.
 * - **Caractéristiques & Prix** — the technical characteristics under the
 *   attribute group's labels, empty ones included, beside one card per price;
 *   a price missing is a « Tarif absent » badge, not an empty cell.
 * - **Stock détaillé** — every depot line as a table (Société, Ville, Point de
 *   vente, Dépôt, Disponible, Réservé, Réel, Dernière mise à jour), with quick
 *   filters on the same line and a summary above. Lines inside the session's
 *   context are marked.
 * - **Tabs** — Équivalents and Documents, in the URL (`?tab=`) so a link to
 *   them is a link.
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useCan } from "@hardauto/core-access";
import type { ApiClient } from "@hardauto/core-api";
import { useAction, useResource } from "@hardauto/core-data";
import { EM_DASH, moment, money, percent, quantity } from "@hardauto/core-i18n";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  DataTable,
  DefinitionList,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  Select,
  Tabs,
  useToast,
  TreeTable,
  type TreeColumn,
  type TreeRow,
} from "@hardauto/core-ui";

import {
  CatalogApi,
  type CategoryLevel,
  type CategoryNode,
  type ConsultationArticle,
  type DepotStock,
  type Fiche,
  type TariffPrice,
} from "../api";
import { NAMESPACE } from "../i18n";
import { CATALOG_PATHS } from "../paths";
import type { ConsultationScope } from "../scope";
import {
  ARTICLE_TABS,
  inScope,
  labelsByPath,
  technicalRows,
  type ArticleTab,
  type TechnicalRow,
} from "../consultation";
import { CategoryTree } from "./categories/CategoryTree";
import { PriceStateBadge, StockStateBadge } from "./consultation/cells";
import { QrCodeButton } from "./consultation/QrCodeButton";
import "./catalog.css";

export interface ConsultationArticleScreenProps {
  client: ApiClient;
  id: string;
  scope: ConsultationScope;
  tab?: string | undefined;
  onTabChange?: ((tab: ArticleTab) => void) | undefined;
}

function scoped(scope: ConsultationScope): boolean {
  return Boolean(scope.companyCode || scope.city || scope.pointOfSaleCode || scope.siteCode);
}

/** Three decimals, the column's own precision, so 0.1 + 0.2 prints as 0.3. */
function sum(lines: readonly DepotStock[], key: "real" | "reserved" | "available"): string {
  return lines.reduce((total, line) => total + Number(line[key]), 0).toFixed(3);
}

export function ConsultationArticleScreen({
  client,
  id,
  scope,
  tab,
  onTabChange,
}: ConsultationArticleScreenProps) {
  const { t } = useTranslation(NAMESPACE);
  const api = useMemo(() => new CatalogApi(client), [client]);

  const fetcher = useCallback(async () => {
    const [article, tree, fiche, levels] = await Promise.all([
      api.consultationArticle(id),
      api.categoryTree(false),
      // The web side — image, documents, the attribute group's fields. Its
      // absence costs the fiche those blocks, never the fiche itself.
      api.fiche(id).catch(() => null),
      // The level names are configuration; without them the levels are
      // numbered rather than missing.
      api.levels().catch(() => ({ items: [] as CategoryLevel[] })),
    ]);
    return {
      article,
      roots: tree.roots,
      labels: labelsByPath(tree.roots),
      fiche,
      levels: Array.isArray(levels.items) ? levels.items : [],
    };
  }, [api, id]);
  const view = useResource(fetcher);

  const active: ArticleTab = (ARTICLE_TABS as readonly string[]).includes(tab ?? "")
    ? (tab as ArticleTab)
    : "equivalents";

  const back = <Link to={CATALOG_PATHS.consultation}>{t("consultation.back")}</Link>;

  if (view.loading && !view.data) {
    return (
      <Page title={t("consultation.article.loadingTitle")} back={back}>
        <LoadingState label={t("consultation.article.loading")} />
      </Page>
    );
  }
  if (view.failed || !view.data) {
    return (
      <Page title={t("consultation.article.loadingTitle")} back={back}>
        <ErrorState title={t("error.title")} onRetry={view.reload} retryLabel={t("error.retry")} />
      </Page>
    );
  }

  const { article, roots, labels, fiche, levels } = view.data;
  const trail = labels.get(article.category_path)?.trail ?? [];
  const short = fiche?.web_designation || article.designation;

  return (
    <Page title={article.reference} eyebrow={article.brand || undefined} back={back}>
      <section className="hardauto-article-hero" aria-label={t("consultation.article.summary")}>
        <ArticleImage api={api} fiche={fiche} label={article.designation} />
        <div className="hardauto-article-hero__body">
          <div className="hardauto-article-hero__title">
            <p className="hardauto-article-hero__short">{short}</p>
            <PublicationBadge status={fiche?.facts.status ?? article.status} />
            <QrCodeButton reference={article.reference} />
          </div>
          {trail.length > 0 ? (
            <p className="hardauto-article-hero__trail">{trail.join(" › ")}</p>
          ) : null}
          <KeyFacts article={article} scope={scope} />
        </div>
      </section>

      <Identification
        api={api}
        article={article}
        fiche={fiche}
        trail={trail}
        roots={roots}
        levels={levels}
        onReclassified={view.reload}
      />

      <div className="hardauto-article-pair">
        <Characteristics article={article} fiche={fiche} />
        <Prices article={article} />
      </div>

      <StockDetail article={article} scope={scope} />

      <Tabs
        label={t("consultation.article.tabs")}
        items={ARTICLE_TABS.map((item) => ({
          id: item,
          label: t(`consultation.article.tab.${item}`),
          count:
            item === "equivalents" ? article.oe_references.length : (fiche?.documents.length ?? 0),
        }))}
        active={active}
        onSelect={(next) => onTabChange?.(next as ArticleTab)}
      />
      {active === "equivalents" ? <Equivalents article={article} /> : null}
      {active === "documents" ? <Documents api={api} fiche={fiche} /> : null}
    </Page>
  );
}

function PublicationBadge({ status }: { status: string }) {
  const { t } = useTranslation(NAMESPACE);
  const tone = status === "published" ? "success" : status === "archived" ? "neutral" : "warning";
  return (
    <Badge tone={tone}>{t(`fiches.status.${status}`, { defaultValue: status || EM_DASH })}</Badge>
  );
}

/**
 * The figures a desk opens the fiche for, in the header so none is a scroll
 * away. Each "no" is its own words: an absent tariff, an unknown stock and a
 * stock at zero are different answers to give a customer.
 */
function KeyFacts({ article, scope }: { article: ConsultationArticle; scope: ConsultationScope }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;

  const counter =
    article.prices.find((line) => line.tariff?.is_default) ??
    article.prices.find((line) => line.tariff === null);
  const narrowing = scoped(scope);
  const lines = narrowing ? article.depots.filter((line) => inScope(line, scope)) : article.depots;
  const available = Number(sum(lines, "available"));
  const stocked = lines.filter((line) => Number(line.available) > 0).length;
  const sold = article.companies.filter((company) => company.in_perimeter).length;

  return (
    <dl className="hardauto-keyfacts" aria-label={t("consultation.article.facts")}>
      <div className="hardauto-keyfacts__item">
        <dt>{counter?.tariff?.label ?? t("consultation.basePrice")}</dt>
        <dd>
          {counter && counter.state === "priced" ? (
            <span className="hardauto-keyfacts__value">{money(counter.unit_price, locale)}</span>
          ) : (
            <PriceStateBadge
              state={counter?.state === "out_of_perimeter" ? counter.state : "missing"}
            />
          )}
        </dd>
      </div>
      <div className="hardauto-keyfacts__item">
        <dt>
          {t("consultation.column.available")}
          {narrowing ? ` · ${t("consultation.article.inContext").toLowerCase()}` : ""}
        </dt>
        <dd>
          {lines.length === 0 ? (
            <StockStateBadge state="unknown" />
          ) : available > 0 ? (
            <span className="hardauto-keyfacts__value">
              {quantity(available.toFixed(3), locale)}{" "}
              <span className="hardauto-keyfacts__unit">{article.sales_unit}</span>
            </span>
          ) : (
            <StockStateBadge state="zero" />
          )}
        </dd>
      </div>
      <div className="hardauto-keyfacts__item">
        <dt>{t("consultation.article.stockedDepots")}</dt>
        <dd>
          <span className="hardauto-keyfacts__value">
            {stocked}
            <span className="hardauto-keyfacts__unit"> / {lines.length}</span>
          </span>
        </dd>
      </div>
      <div className="hardauto-keyfacts__item">
        <dt>{t("consultation.article.perimeter")}</dt>
        <dd>
          <span className="hardauto-keyfacts__value">
            {sold}
            <span className="hardauto-keyfacts__unit"> / {article.companies.length}</span>
          </span>{" "}
          <span className="hardauto-cell__muted">{t("consultation.article.soldHint")}</span>
        </dd>
      </div>
    </dl>
  );
}

/**
 * The article's picture: the web image address when there is one, the
 * primary uploaded photo otherwise, a placeholder when neither — so the header
 * keeps its shape and the reader sees that there is no picture.
 */
function ArticleImage({
  api,
  fiche,
  label,
}: {
  api: CatalogApi;
  fiche: Fiche | null;
  label: string;
}) {
  const { t } = useTranslation(NAMESPACE);
  const photo = fiche ? (fiche.photos.find((item) => item.is_primary) ?? fiche.photos[0]) : null;
  const address =
    fiche?.web_image_url && /^https?:\/\//i.test(fiche.web_image_url) ? fiche.web_image_url : "";
  const blob = useObjectUrl(address ? null : (photo?.file_id ?? null), api);
  const source = address || blob;

  return (
    <div className="hardauto-article-hero__image">
      {source ? (
        <img src={source} alt={label} />
      ) : (
        <span className="hardauto-article-hero__placeholder">
          {t("consultation.article.noImage")}
        </span>
      )}
    </div>
  );
}

/** An object URL for a stored file, revoked when it is no longer shown. */
function useObjectUrl(fileId: string | null, api: CatalogApi): string {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!fileId) {
      setUrl("");
      return undefined;
    }
    let made = "";
    let live = true;
    api
      .fileBlob(fileId)
      .then((blob) => {
        if (!live) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      })
      .catch(() => {
        if (live) setUrl("");
      });
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [fileId, api]);
  return url;
}

// --- Block 1: identification ----------------------------------------------------

function Identification({
  api,
  article,
  fiche,
  trail,
  roots,
  levels,
  onReclassified,
}: {
  api: CatalogApi;
  article: ConsultationArticle;
  fiche: Fiche | null;
  trail: string[];
  roots: CategoryNode[];
  levels: CategoryLevel[];
  onReclassified: () => void;
}) {
  const { t } = useTranslation(NAMESPACE);
  const mayClassify = useCan("catalog.enrichment.write");
  const [classifying, setClassifying] = useState(false);
  const opener = useRef<HTMLButtonElement>(null);

  // One row per level the installation names, filled from the article's path:
  // Activité, Famille, Sous-famille, Segment. Without names, numbered.
  const depth = Math.max(levels.length, trail.length);
  const levelRows = Array.from({ length: depth }, (_, index) => ({
    label:
      levels.find((level) => level.depth === index + 1)?.label ||
      t("panel.level", { depth: index + 1 }),
    value: trail[index] ?? EM_DASH,
  }));

  return (
    <Card
      title={t("consultation.article.identity")}
      actions={
        mayClassify && fiche ? (
          <Button ref={opener} size="sm" onClick={() => setClassifying(true)}>
            {t("consultation.article.reclassify")}
          </Button>
        ) : undefined
      }
    >
      <div className="hardauto-article-identity">
        <DefinitionList
          layout="inline"
          items={[
            { label: t("consultation.column.reference"), value: <code>{article.reference}</code> },
            { label: t("consultation.article.erpDesignation"), value: article.designation },
            {
              label: t("consultation.article.webDesignation"),
              value: fiche?.web_designation || (
                <span className="hardauto-cell__muted">{t("consultation.article.notFilled")}</span>
              ),
            },
            { label: t("consultation.article.unit"), value: article.sales_unit },
            { label: t("consultation.brand"), value: article.brand || EM_DASH },
            {
              label: t("consultation.article.family"),
              value: article.family_label
                ? `${article.family_label} (${article.family_code})`
                : article.family_code || EM_DASH,
            },
            {
              label: t("consultation.article.referenceBase"),
              value: article.reference_base || EM_DASH,
            },
            {
              label: t("consultation.article.codeFournisseur"),
              value: article.code_fournisseur || EM_DASH,
            },
            { label: t("consultation.article.ipc"), value: article.ipc || EM_DASH },
          ]}
        />
        <DefinitionList
          layout="inline"
          items={[
            ...levelRows,
            {
              label: t("consultation.article.category"),
              value:
                trail.length > 0 ? (
                  <span className="hardauto-article-identity__path">
                    {trail.join(" › ")}
                    {fiche?.category_inherited ? (
                      <span className="hardauto-cell__muted">
                        {" "}
                        · {t("consultation.article.inherited")}
                      </span>
                    ) : null}
                  </span>
                ) : (
                  <Badge tone="warning">{t("consultation.unclassified")}</Badge>
                ),
            },
            {
              label: t("consultation.article.publication"),
              value: <PublicationBadge status={fiche?.facts.status ?? article.status} />,
            },
          ]}
        />
      </div>

      {classifying && fiche ? (
        <ClassificationPanel
          api={api}
          articleId={article.id}
          fiche={fiche}
          roots={roots}
          levels={levels}
          onClose={() => {
            setClassifying(false);
            opener.current?.focus();
          }}
          onSaved={() => {
            setClassifying(false);
            onReclassified();
          }}
        />
      ) : null}
    </Card>
  );
}

/**
 * The whole tree in a side panel, to choose or correct the article's place.
 *
 * A side panel rather than a select: 234 nodes do not fit in a drop-down, and
 * the reader needs the search and the shape of the tree to find the right
 * segment. Escape and the backdrop close it; focus returns to the button
 * that opened it.
 */
function ClassificationPanel({
  api,
  articleId,
  fiche,
  roots,
  levels,
  onClose,
  onSaved,
}: {
  api: CatalogApi;
  articleId: string;
  fiche: Fiche;
  roots: CategoryNode[];
  levels: CategoryLevel[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const toast = useToast();
  const action = useAction();
  const titleId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const [chosen, setChosen] = useState<CategoryNode | null>(null);
  const none = useMemo(() => new Map(), []);
  const noneSet = useMemo(() => new Set<string>(), []);

  useEffect(() => {
    heading.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  function save(body: { category_id: string } | { clear_category: true }) {
    void action.run("classify", async () => {
      try {
        await api.saveFiche(articleId, body);
        toast.show(t("consultation.article.reclassified"), { tone: "success" });
        onSaved();
      } catch {
        toast.show(t("error.title"), { tone: "danger" });
      }
    });
  }

  return (
    <div className="hardauto-drawer">
      <button
        type="button"
        className="hardauto-drawer__backdrop"
        aria-label={t("categories.cancel")}
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        className="hardauto-drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="hardauto-drawer__head">
          <h2 id={titleId} ref={heading} tabIndex={-1} className="hardauto-drawer__title">
            {t("consultation.article.reclassify")}
          </h2>
          <p className="hardauto-cell__muted">
            {fiche.category_path
              ? t("consultation.article.currentPlace", { place: fiche.category_label })
              : t("consultation.unclassified")}
            {fiche.category_inherited ? ` · ${t("consultation.article.inherited")}` : ""}
          </p>
        </header>
        <div className="hardauto-drawer__body">
          <CategoryTree
            roots={roots}
            locale={i18n.language}
            selectedId={chosen?.category.id ?? fiche.category_id}
            families={none}
            unmapped={noneSet}
            levelLabel={(depth) => levels.find((level) => level.depth === depth)?.label ?? ""}
            mayWrite={false}
            onSelect={setChosen}
            onAddChild={() => undefined}
          />
        </div>
        <footer className="hardauto-drawer__foot">
          <Button
            variant="primary"
            disabled={!chosen || chosen.category.id === fiche.category_id}
            busy={action.isBusy("classify")}
            busyLabel={t("panel.saving")}
            onClick={() => chosen && save({ category_id: chosen.category.id })}
          >
            {chosen
              ? t("consultation.article.classifyIn", { place: chosen.category.label })
              : t("consultation.article.chooseCategory")}
          </Button>
          {/* Only an explicit classification can be undone; an inherited one
              or none at all has nothing to go back from. */}
          {fiche.category_id && !fiche.category_inherited ? (
            <Button onClick={() => save({ clear_category: true })}>
              {t("consultation.article.backToFamily")}
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            {t("categories.cancel")}
          </Button>
        </footer>
      </div>
    </div>
  );
}

// --- Block 2: characteristics and prices ---------------------------------------

function Characteristics({
  article,
  fiche,
}: {
  article: ConsultationArticle;
  fiche: Fiche | null;
}) {
  const { t } = useTranslation(NAMESPACE);
  const group = fiche?.attribute_group ?? null;
  const rows = technicalRows(
    group?.attributes ?? [],
    fiche ? fiche.facts.attributes : article.characteristics,
  );

  return (
    <Card
      title={
        group
          ? t("consultation.article.technicalOf", { group: group.label })
          : t("consultation.article.characteristics")
      }
      flush
    >
      <DataTable<TechnicalRow>
        caption={t("consultation.article.characteristics")}
        density="compact"
        columns={[
          {
            key: "label",
            header: t("consultation.article.attribute"),
            render: (row) =>
              row.declared ? row.label : <span className="hardauto-cell__muted">{row.label}</span>,
          },
          {
            key: "value",
            header: t("consultation.article.value"),
            render: (row) =>
              row.value ? (
                row.value
              ) : (
                <span className="hardauto-cell__muted">{t("consultation.article.notFilled")}</span>
              ),
          },
        ]}
        rows={rows}
        rowKey={(row) => row.key}
        loadingLabel={t("consultation.article.loading")}
        empty={{ title: t("consultation.article.noCharacteristics") }}
      />
    </Card>
  );
}

/**
 * One card per price the system knows: the base price, then each tariff, the
 * counter one marked. A missing price is a badge on its card — the card stays,
 * so "this tariff has no price for it" is visible rather than inferred.
 */
export function Prices({ article }: { article: ConsultationArticle }) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const ordered = [
    ...article.prices.filter((line) => line.tariff === null),
    ...article.prices.filter((line) => line.tariff !== null),
  ];

  return (
    <Card title={t("consultation.article.tab.prices")}>
      <ul className="hardauto-pricecards" aria-label={t("consultation.article.tab.prices")}>
        {ordered.map((line: TariffPrice) => (
          <li key={line.tariff?.code ?? "__base__"} className="hardauto-pricecard">
            <span className="hardauto-pricecard__label">
              {line.tariff ? line.tariff.label : t("consultation.basePrice")}
              {line.tariff?.is_default ? (
                <Badge tone="info">{t("consultation.article.defaultTariff")}</Badge>
              ) : null}
            </span>
            {line.state === "priced" ? (
              <span className="hardauto-pricecard__value">{money(line.unit_price, locale)}</span>
            ) : (
              <PriceStateBadge state={line.state === "out_of_perimeter" ? line.state : "missing"} />
            )}
            {line.state === "priced" && Number(line.discount_pct) > 0 ? (
              <span className="hardauto-cell__muted">
                {t("consultation.article.discountOf", {
                  discount: percent(line.discount_pct, locale),
                  base: money(line.base_price, locale),
                })}
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      <p className="hardauto-catalog__note">
        {article.default_tariff
          ? t("consultation.article.sharedTariff", { tariff: article.default_tariff.label })
          : t("consultation.article.noDefaultTariff")}
      </p>
    </Card>
  );
}

// --- Block 3: stock --------------------------------------------------------------

/** A row of the stock table: a location, a point of sale in it, or one depot. */
interface StockNode {
  name: string;
  companies: string[];
  available: number;
  reserved: number;
  real: number;
  /** Set on a depot row only. */
  line?: DepotStock;
}

/** Location → point of sale → depot, each level carrying the totals of what is under it. */
function stockRows(
  lines: readonly DepotStock[],
  noLocation: string,
  noPoint: string,
  locale: string,
): TreeRow<StockNode>[] {
  const total = (list: readonly DepotStock[]) => ({
    available: list.reduce((sum, line) => sum + Number(line.available), 0),
    reserved: list.reduce((sum, line) => sum + Number(line.reserved), 0),
    real: list.reduce((sum, line) => sum + Number(line.real), 0),
  });
  const companiesOf = (list: readonly DepotStock[]) => [
    ...new Set(list.map((line) => line.company_name || line.company_code).filter(Boolean)),
  ];
  const group = <K,>(list: readonly DepotStock[], key: (line: DepotStock) => K) => {
    const map = new Map<K, DepotStock[]>();
    for (const line of list) map.set(key(line), [...(map.get(key(line)) ?? []), line]);
    return [...map.entries()];
  };
  return group(lines, (line) => line.city || "")
    .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b, locale)))
    .map(([city, inCity]) => ({
      key: `city:${city}`,
      node: { name: city || noLocation, companies: companiesOf(inCity), ...total(inCity) },
      children: group(inCity, (line) => line.point_of_sale_code || "")
        .sort(([, a], [, b]) =>
          (a[0]?.point_of_sale_name ?? "").localeCompare(b[0]?.point_of_sale_name ?? "", locale),
        )
        .map(([point, inPoint]) => ({
          key: `city:${city}/pos:${point}`,
          node: {
            name: inPoint[0]?.point_of_sale_name || point || noPoint,
            companies: companiesOf(inPoint),
            ...total(inPoint),
          },
          children: [...inPoint]
            .sort((a, b) => a.site_name.localeCompare(b.site_name, locale))
            .map((line) => ({
              key: `city:${city}/pos:${point}/dep:${line.site_code}`,
              node: {
                name: line.site_name,
                companies: companiesOf([line]),
                ...total([line]),
                line,
              },
            })),
        })),
    }));
}

export function StockDetail({
  article,
  scope,
}: {
  article: ConsultationArticle;
  scope: ConsultationScope;
}) {
  const { t, i18n } = useTranslation(NAMESPACE);
  const locale = i18n.language;
  const narrowing = scoped(scope);

  const [company, setCompany] = useState("");
  const [city, setCity] = useState("");
  const [point, setPoint] = useState("");
  const [site, setSite] = useState("");
  const [withStock, setWithStock] = useState(false);

  const lines = article.depots;
  const distinct = (pick: (line: DepotStock) => [string, string]) =>
    [...new Map(lines.map(pick).filter(([code]) => code)).entries()].sort((a, b) =>
      a[1].localeCompare(b[1], locale),
    );
  // Each filter offers only what the broader ones leave, as the stock screen.
  const byCompany = lines.filter((line) => !company || line.company_code === company);
  const byCity = byCompany.filter((line) => !city || line.city === city);
  const byPoint = byCity.filter((line) => !point || line.point_of_sale_code === point);
  // Grouped by where the stock is — location, then point of sale, then depot —
  // rather than by who owns it: the question is "where can I get it".
  const shown = byPoint
    .filter((line) => !site || line.site_code === site)
    .filter((line) => !withStock || Number(line.available) > 0)
    .sort(
      (a, b) =>
        a.city.localeCompare(b.city, locale) ||
        a.point_of_sale_name.localeCompare(b.point_of_sale_name, locale) ||
        a.site_name.localeCompare(b.site_name, locale),
    );

  const companies = distinct((line) => [line.company_code, line.company_name || line.company_code]);
  const cities = [...new Set(byCompany.map((line) => line.city).filter(Boolean))].sort((a, b) =>
    a.localeCompare(b, locale),
  );
  const points = [
    ...new Map(
      byCity
        .filter((line) => line.point_of_sale_code)
        .map((line) => [
          line.point_of_sale_code,
          line.point_of_sale_name || line.point_of_sale_code,
        ]),
    ).entries(),
  ];
  const sites = byPoint.map((line) => [line.site_code, line.site_name] as const);

  const qty = (value: string) => quantity(Number(value).toFixed(3), locale);
  const sold = article.companies.filter((item) => item.in_perimeter).length;
  const stocked = shown.filter((line) => Number(line.available) > 0).length;

  const rows = stockRows(
    shown,
    t("consultation.article.noLocation"),
    t("consultation.pointOfSale"),
    locale,
  );
  const columns: TreeColumn<StockNode>[] = [
    {
      key: "place",
      header: t("consultation.article.placeColumn"),
      render: (node) =>
        node.line ? (
          <span className="hardauto-cell__stack">
            <span>{node.name}</span>
            {narrowing && inScope(node.line, scope) ? (
              <span className="hardauto-cell__muted">{t("consultation.article.inContext")}</span>
            ) : null}
          </span>
        ) : (
          node.name
        ),
    },
    {
      key: "company",
      header: t("consultation.article.company"),
      render: (node) => node.companies.join(", ") || EM_DASH,
    },
    {
      key: "available",
      header: t("consultation.column.available"),
      align: "end",
      render: (node) =>
        node.available > 0 ? (
          <span className="hardauto-num hardauto-num--strong">{qty(String(node.available))}</span>
        ) : (
          <Badge tone="danger">{t("consultation.stock.zero")}</Badge>
        ),
    },
    {
      key: "reserved",
      header: t("consultation.article.reserved"),
      align: "end",
      render: (node) => <span className="hardauto-num">{qty(String(node.reserved))}</span>,
    },
    {
      key: "real",
      header: t("consultation.article.real"),
      align: "end",
      render: (node) => <span className="hardauto-num">{qty(String(node.real))}</span>,
    },
    {
      key: "updated",
      header: t("consultation.article.updatedAt"),
      render: (node) =>
        node.line ? (
          <span className="hardauto-cell__muted">
            {moment(node.line.updated_at ?? null, locale)}
          </span>
        ) : null,
    },
  ];

  return (
    <Card title={t("consultation.article.stockDetail")}>
      {lines.length === 0 ? (
        <EmptyState
          title={t("consultation.stock.unknown")}
          description={t("consultation.article.noDepots")}
        />
      ) : (
        <div className="hardauto-stack">
          <dl className="hardauto-article-stock__summary">
            <div>
              <dt>{t("consultation.article.stockTotal")}</dt>
              <dd>
                {qty(sum(shown, "available"))} <small>{article.sales_unit}</small>
              </dd>
            </div>
            <div>
              <dt>{t("consultation.article.stockedDepots")}</dt>
              <dd>
                {stocked} <small>/ {shown.length}</small>
              </dd>
            </div>
            <div>
              <dt>{t("consultation.article.sellingCompanies")}</dt>
              <dd>
                {sold} <small>/ {article.companies.length}</small>
              </dd>
            </div>
          </dl>

          <div
            className="hardauto-article-stock__filters"
            role="group"
            aria-label={t("consultation.article.stockFilters")}
          >
            <Select
              aria-label={t("consultation.city")}
              value={city}
              onChange={(event) => {
                setCity(event.target.value);
                setPoint("");
                setSite("");
              }}
            >
              <option value="">{t("consultation.article.allCities")}</option>
              {cities.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </Select>
            <Select
              aria-label={t("consultation.pointOfSale")}
              value={point}
              onChange={(event) => {
                setPoint(event.target.value);
                setSite("");
              }}
            >
              <option value="">{t("consultation.article.allPoints")}</option>
              {points.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Select>
            <Select
              aria-label={t("consultation.depot")}
              value={site}
              onChange={(event) => setSite(event.target.value)}
            >
              <option value="">{t("consultation.article.allSites")}</option>
              {sites.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Select>
            <Select
              aria-label={t("consultation.article.company")}
              value={company}
              onChange={(event) => {
                setCompany(event.target.value);
                setCity("");
                setPoint("");
                setSite("");
              }}
            >
              <option value="">{t("consultation.article.allCompanies")}</option>
              {companies.map(([code, name]) => (
                <option key={code} value={code}>
                  {name}
                </option>
              ))}
            </Select>
            <Checkbox
              label={t("consultation.article.withStockOnly")}
              checked={withStock}
              onChange={(event) => setWithStock(event.target.checked)}
            />
          </div>

          {rows.length === 0 ? (
            <EmptyState title={t("consultation.article.noStockMatch")} />
          ) : (
            <TreeTable<StockNode>
              caption={t("consultation.article.stockDetail")}
              columns={columns}
              rows={rows}
              toggleLabel={(node, open) =>
                `${open ? t("consultation.article.collapse") : t("consultation.article.expand")} ${node.name}`
              }
            />
          )}
        </div>
      )}
    </Card>
  );
}

// --- Tabs: what is secondary ------------------------------------------------------

/** The references this article stands in for — the constructor's numbers. */
function Equivalents({ article }: { article: ConsultationArticle }) {
  const { t } = useTranslation(NAMESPACE);
  if (article.oe_references.length === 0) {
    return <EmptyState title={t("consultation.article.noEquivalents")} />;
  }
  return (
    <Card title={t("consultation.article.oe")}>
      <ul className="hardauto-refs">
        {article.oe_references.map((reference) => (
          <li key={reference}>
            <code>{reference}</code>
          </li>
        ))}
      </ul>
    </Card>
  );
}

function Documents({ api, fiche }: { api: CatalogApi; fiche: Fiche | null }) {
  const { t } = useTranslation(NAMESPACE);
  const toast = useToast();
  const documents = fiche?.documents ?? [];

  async function open(fileId: string) {
    // Opened before the bytes arrive, so the browser treats it as the click's
    // own window rather than a pop-up; filled in once they do.
    const target = window.open("", "_blank");
    try {
      const url = URL.createObjectURL(await api.fileBlob(fileId));
      if (target) target.location.href = url;
      else window.location.href = url;
    } catch {
      target?.close();
      toast.show(t("consultation.article.openFailed"), { tone: "danger" });
    }
  }

  if (documents.length === 0) {
    return <EmptyState title={t("consultation.article.noDocuments")} />;
  }
  return (
    <Card title={t("consultation.article.documents")}>
      <ul className="hardauto-article-docs">
        {documents.map((item) => (
          <li key={item.id}>
            <button
              type="button"
              className="hardauto-article-docs__link"
              onClick={() => void open(item.file_id)}
            >
              {item.label || t("consultation.article.document")}
            </button>
            <span className="hardauto-cell__muted">{item.content_type}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
}
